import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG as QRCode } from "qrcode.react";
import { renderToStaticMarkup } from "react-dom/server";
import JSZip from "jszip";

import {
    DownloadBlackIcon,
    DownloadWhiteIcon,
    PrinterIcon,
    CopyIcon,
    ShareIcon,
} from "../../../assets/icons";

import { useParams } from "react-router-dom";
import { useNotification } from "../../toster-notification/NotificationContext";
import { LocalQRCodeDataDTO } from "../../../models/local/local.response";
import { localService } from "../../../services/localService";

const CLIENT_APP_URL = "https://app.quickmenu.rs";

type QRCodeTarget = "menu" | "links" | "table";

interface QRExportItem {
    id: string;
    title: string;
    description: string;
    value: string;
    fileName: string;
}

const isValidHexColor = (value: string) =>
    /^#[0-9A-Fa-f]{6}$/.test(value);

const sanitizeFileName = (value: string) =>
    value
        .trim()
        .toLowerCase()
        .replace(/č/g, "c")
        .replace(/ć/g, "c")
        .replace(/š/g, "s")
        .replace(/ž/g, "z")
        .replace(/đ/g, "dj")
        .replace(/[^a-z0-9-_]+/g, "-")
        .replace(/^-+|-+$/g, "");

const escapeHtml = (value: string) =>
    value.replace(/[&<>"']/g, character => {
        const replacements: Record<string, string> = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#039;",
        };

        return replacements[character];
    });

const QRCodePage: React.FC = () => {
    const { localId } = useParams<{ localId: string }>();
    const { notify } = useNotification();

    const [data, setData] = useState<LocalQRCodeDataDTO | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isExportingAll, setIsExportingAll] = useState(false);

    const [qrTarget, setQrTarget] = useState<QRCodeTarget>("menu");
    const [selectedTableId, setSelectedTableId] = useState("");

    const [qrSize, setQrSize] = useState(300);
    const [qrColor, setQrColor] = useState("#000000");
    const [bgColor, setBgColor] = useState("#ffffff");

    const QR_SIZES = [
        { value: 200, label: "Mala (200x200px)" },
        { value: 300, label: "Srednja (300x300px)" },
        { value: 500, label: "Velika (500x500px)" },
        { value: 1000, label: "Ekstra velika (1000x1000px)" },
    ];

    const safeQrColor = isValidHexColor(qrColor)
        ? qrColor
        : "#000000";

    const safeBgColor = isValidHexColor(bgColor)
        ? bgColor
        : "#ffffff";

    const menuUrl = localId
        ? `${CLIENT_APP_URL}/client-menu/${localId}/online`
        : "";

    const linksPageUrl = data?.slug
        ? `${CLIENT_APP_URL}/${data.slug}/links`
        : "";

    const tableQrCodeBaseUrl = localId
        ? `${CLIENT_APP_URL}/client-menu/${localId}?tableId=`
        : "";

    const sortedTables = useMemo(() => {
        return [...(data?.tables ?? [])].sort(
            (firstTable, secondTable) =>
                firstTable.number - secondTable.number
        );
    }, [data]);

    useEffect(() => {
        if (!sortedTables.length) {
            setSelectedTableId("");
            return;
        }

        const selectedTableStillExists = sortedTables.some(
            table => table.id === selectedTableId
        );

        if (!selectedTableStillExists) {
            setSelectedTableId(sortedTables[0].id);
        }
    }, [sortedTables, selectedTableId]);

    useEffect(() => {
        if (!localId) {
            notify(
                "error",
                "Nedostaje ID lokala",
                "Nije moguće generisati QR kod bez ID-ja lokala."
            );

            setIsLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);

                const response = await localService.getLocalDataForQrCode(localId);

                if (response.success && response.data) {
                    setData(response.data);
                } else {
                    notify(
                        "error",
                        "Greška pri učitavanju podataka",
                        "Došlo je do greške prilikom učitavanja podataka za QR kod."
                    );
                }
            } catch (error) {
                console.error(
                    "Greška pri učitavanju QR podataka:",
                    error
                );

                notify(
                    "error",
                    "Greška pri učitavanju podataka",
                    "Došlo je do greške prilikom učitavanja podataka za QR kod."
                );
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [localId, notify]);

    const currentQrCode = useMemo<QRExportItem | null>(() => {
        const localName = data?.slug || localId || "lokal";

        if (qrTarget === "menu") {
            if (!menuUrl) {
                return null;
            }

            return {
                id: "menu",
                title: "Digitalni meni",
                description: "Skenirajte za pristup digitalnom meniju.",
                value: menuUrl,
                fileName: `quickmenu-${localName}-meni`,
            };
        }

        if (qrTarget === "links") {
            if (!linksPageUrl) {
                return null;
            }

            return {
                id: "links",
                title: "Stranica sa linkovima",
                description:
                    "Skenirajte za pristup stranici sa linkovima lokala.",
                value: linksPageUrl,
                fileName: `quickmenu-${localName}-linkovi`,
            };
        }

        const selectedTable = sortedTables.find(
            table => table.id === selectedTableId
        );

        if (!selectedTable || !tableQrCodeBaseUrl) {
            return null;
        }

        return {
            id: selectedTable.id,
            title: `Sto broj ${selectedTable.number}`,
            description: `Skenirajte za pristup meniju za sto broj ${selectedTable.number}.`,
            value: `${tableQrCodeBaseUrl}${selectedTable.id}`,
            fileName: `quickmenu-${localName}-sto-${selectedTable.number}`,
        };
    }, [
        qrTarget,
        data,
        localId,
        menuUrl,
        linksPageUrl,
        tableQrCodeBaseUrl,
        selectedTableId,
        sortedTables,
    ]);

    const allQrCodes = useMemo<QRExportItem[]>(() => {
        if (!data || !localId) {
            return [];
        }

        const safeSlug = sanitizeFileName(data.slug || localId);

        const result: QRExportItem[] = [
            {
                id: "menu",
                title: "Digitalni meni",
                description: "Direktan pristup digitalnom meniju.",
                value: menuUrl,
                fileName: `quickmenu-${safeSlug}-meni`,
            },
            {
                id: "links",
                title: "Stranica sa linkovima",
                description:
                    "Pristup stranici sa linkovima lokala.",
                value: linksPageUrl,
                fileName: `quickmenu-${safeSlug}-linkovi`,
            },
        ];

        sortedTables.forEach(table => {
            result.push({
                id: table.id,
                title: `Sto broj ${table.number}`,
                description: `Pristup meniju za sto broj ${table.number}.`,
                value: `${tableQrCodeBaseUrl}${table.id}`,
                fileName: `quickmenu-${safeSlug}-sto-${table.number}`,
            });
        });

        return result.filter(item => Boolean(item.value));
    }, [
        data,
        localId,
        menuUrl,
        linksPageUrl,
        tableQrCodeBaseUrl,
        sortedTables,
    ]);

    const buildQrSvg = (value: string) => {
        let svgContent = renderToStaticMarkup(
            <QRCode
                value={value}
                size={qrSize}
                fgColor={safeQrColor}
                bgColor={safeBgColor}
                level="H"
                includeMargin
            />
        );

        if (!svgContent.includes("xmlns=")) {
            svgContent = svgContent.replace(
                "<svg",
                '<svg xmlns="http://www.w3.org/2000/svg"'
            );
        }

        return svgContent;
    };

    const downloadBlob = (blob: Blob, fileName: string) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");

        link.href = url;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.setTimeout(() => {
            URL.revokeObjectURL(url);
        }, 1000);
    };

    const convertSvgToPngBlob = (
        svgContent: string
    ): Promise<Blob> => {
        return new Promise((resolve, reject) => {
            const svgBlob = new Blob([svgContent], {
                type: "image/svg+xml;charset=utf-8",
            });

            const svgUrl = URL.createObjectURL(svgBlob);
            const image = new Image();

            image.onload = () => {
                const canvas = document.createElement("canvas");

                canvas.width = qrSize;
                canvas.height = qrSize;

                const context = canvas.getContext("2d");

                if (!context) {
                    URL.revokeObjectURL(svgUrl);
                    reject(
                        new Error(
                            "Nije moguće kreirati canvas context."
                        )
                    );
                    return;
                }

                context.fillStyle = safeBgColor;
                context.fillRect(0, 0, qrSize, qrSize);

                context.drawImage(image, 0, 0, qrSize, qrSize);

                canvas.toBlob(blob => {
                    URL.revokeObjectURL(svgUrl);

                    if (!blob) {
                        reject(
                            new Error(
                                "Nije moguće generisati PNG dokument."
                            )
                        );
                        return;
                    }

                    resolve(blob);
                }, "image/png");
            };

            image.onerror = () => {
                URL.revokeObjectURL(svgUrl);
                reject(
                    new Error(
                        "Došlo je do greške prilikom učitavanja SVG dokumenta."
                    )
                );
            };

            image.src = svgUrl;
        });
    };

    const handleCopyLink = async () => {
        if (!currentQrCode) {
            return;
        }

        try {
            await navigator.clipboard.writeText(
                currentQrCode.value
            );

            notify(
                "info",
                "Link je kopiran",
                "Link QR koda je kopiran u clipboard."
            );
        } catch (error) {
            console.error("Greška pri kopiranju linka:", error);

            notify(
                "error",
                "Link nije kopiran",
                "Došlo je do greške prilikom kopiranja linka."
            );
        }
    };

    const handleShareLink = async () => {
        if (!currentQrCode) {
            return;
        }

        if (!navigator.share) {
            await handleCopyLink();
            return;
        }

        try {
            await navigator.share({
                title: `QuickMenu - ${currentQrCode.title}`,
                text: currentQrCode.description,
                url: currentQrCode.value,
            });
        } catch (error) {
            if (
                error instanceof DOMException &&
                error.name === "AbortError"
            ) {
                return;
            }

            console.error("Greška pri deljenju linka:", error);
        }
    };

    const handleDownloadPNG = async () => {
        if (!currentQrCode) {
            return;
        }

        try {
            const svgContent = buildQrSvg(
                currentQrCode.value
            );

            const pngBlob =
                await convertSvgToPngBlob(svgContent);

            downloadBlob(
                pngBlob,
                `${sanitizeFileName(
                    currentQrCode.fileName
                )}.png`
            );
        } catch (error) {
            console.error(
                "Greška pri generisanju PNG QR koda:",
                error
            );

            notify(
                "error",
                "PNG nije generisan",
                "Došlo je do greške prilikom generisanja PNG QR koda."
            );
        }
    };

    const handleDownloadSVG = () => {
        if (!currentQrCode) {
            return;
        }

        const svgContent = buildQrSvg(currentQrCode.value);

        const svgBlob = new Blob([svgContent], {
            type: "image/svg+xml;charset=utf-8",
        });

        downloadBlob(
            svgBlob,
            `${sanitizeFileName(currentQrCode.fileName)}.svg`
        );
    };

    const handleDownloadAllQrCodes = async () => {
        if (!allQrCodes.length || !data) {
            notify(
                "error",
                "Nema QR kodova",
                "Nema dostupnih QR kodova za izvoz."
            );

            return;
        }

        try {
            setIsExportingAll(true);

            const zip = new JSZip();
            const qrFolder = zip.folder("qr-kodovi");

            allQrCodes.forEach(qrCode => {
                const svgContent = buildQrSvg(qrCode.value);

                qrFolder?.file(
                    `${sanitizeFileName(qrCode.fileName)}.svg`,
                    svgContent
                );
            });

            const linksContent = allQrCodes
                .map(qrCode => {
                    return `${qrCode.title}\n${qrCode.value}`;
                })
                .join("\n\n");

            qrFolder?.file("linkovi.txt", linksContent);

            const zipBlob = await zip.generateAsync({
                type: "blob",
            });

            const safeSlug = sanitizeFileName(
                data.slug || localId || "lokal"
            );

            downloadBlob(
                zipBlob,
                `quickmenu-${safeSlug}-qr-kodovi.zip`
            );

            notify(
                "info",
                "QR kodovi su generisani",
                `Izvezeno je ${allQrCodes.length} QR kodova.`
            );
        } catch (error) {
            console.error(
                "Greška pri izvozu svih QR kodova:",
                error
            );

            notify(
                "error",
                "Izvoz nije uspeo",
                "Došlo je do greške prilikom izvoza QR kodova."
            );
        } finally {
            setIsExportingAll(false);
        }
    };

    const handlePrint = () => {
        if (!currentQrCode) {
            return;
        }

        const svgContent = buildQrSvg(currentQrCode.value);
        const printWindow = window.open(
            "",
            "_blank",
            "width=800,height=900"
        );

        if (!printWindow) {
            notify(
                "error",
                "Prozor nije otvoren",
                "Dozvolite pop-up prozore kako biste odštampali QR kod."
            );

            return;
        }

        printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="sr">
                <head>
                    <meta charset="UTF-8" />
                    <title>${escapeHtml(currentQrCode.title)}</title>

                    <style>
                        * {
                            box-sizing: border-box;
                        }

                        body {
                            min-height: 100vh;
                            margin: 0;
                            padding: 40px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-family: Arial, sans-serif;
                            color: #111111;
                        }

                        .qr-print-wrapper {
                            max-width: 600px;
                            width: 100%;
                            text-align: center;
                        }

                        h1 {
                            margin: 0 0 12px;
                            font-size: 30px;
                        }

                        p {
                            margin: 0 0 24px;
                            font-size: 17px;
                        }

                        svg {
                            width: 100%;
                            max-width: 420px;
                            height: auto;
                        }

                        .qr-url {
                            margin-top: 22px;
                            font-size: 12px;
                            word-break: break-all;
                            color: #555555;
                        }

                        @media print {
                            body {
                                padding: 0;
                            }
                        }
                    </style>
                </head>

                <body>
                    <main class="qr-print-wrapper">
                        <h1>${escapeHtml(currentQrCode.title)}</h1>

                        <p>
                            ${escapeHtml(
            currentQrCode.description
        )}
                        </p>

                        ${svgContent}

                        <div class="qr-url">
                            ${escapeHtml(currentQrCode.value)}
                        </div>
                    </main>

                    <script>
                        window.onload = function () {
                            window.print();
                        };
                    </script>
                </body>
            </html>
        `);

        printWindow.document.close();
    };

    const handleQrColorTextChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;

        if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
            setQrColor(value);
        }
    };

    const handleBgColorTextChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = event.target.value;

        if (/^#[0-9A-Fa-f]{0,6}$/.test(value)) {
            setBgColor(value);
        }
    };

    return (
        <section className="qr-code-section">
            <div className="w-layout-blockcontainer container w-container">
                <div className="sttings-content-wrapper">
                    <div className="header-div">
                        <div>
                            <h2 className="heading-3">
                                QR kodovi
                            </h2>

                            <div className="text-block">
                                Generišite QR kod za meni,
                                stranicu sa linkovima ili
                                pojedinačne stolove.
                            </div>
                        </div>
                    </div>

                    <div className="qr-code-settings-block">
                        <div className="qr-code-specs-div">
                            <div className="admin-settings-box-div">
                                <div className="list-header">
                                    <div className="list-header-info">
                                        <div className="list-header-title color">
                                            Podešavanja QR koda
                                        </div>

                                        <div className="list-header-description">
                                            Izaberite sadržaj i
                                            prilagodite izgled QR
                                            koda.
                                        </div>
                                    </div>
                                </div>

                                <div className="qr-code-style-list">
                                    <div className="form-block-2 w-form">
                                        <div className="form-5">
                                            <div className="color-pick-title">
                                                Vrsta QR koda
                                            </div>

                                            <select
                                                className="form-select qr-code-size w-select"
                                                value={qrTarget}
                                                onChange={event =>
                                                    setQrTarget(
                                                        event.target
                                                            .value as QRCodeTarget
                                                    )
                                                }
                                            >
                                                <option value="menu">
                                                    Digitalni meni
                                                </option>

                                                <option value="links">
                                                    Stranica sa
                                                    linkovima
                                                </option>

                                                <option value="table">
                                                    QR kod za sto
                                                </option>
                                            </select>
                                        </div>
                                    </div>

                                    {qrTarget === "table" && (
                                        <div className="form-block-2 w-form">
                                            <div className="form-5">
                                                <div className="color-pick-title">
                                                    Izaberite sto
                                                </div>

                                                {sortedTables.length >
                                                    0 ? (
                                                    <select
                                                        className="form-select qr-code-size w-select"
                                                        value={
                                                            selectedTableId
                                                        }
                                                        onChange={event =>
                                                            setSelectedTableId(
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                    >
                                                        {sortedTables.map(
                                                            table => (
                                                                <option
                                                                    key={
                                                                        table.id
                                                                    }
                                                                    value={
                                                                        table.id
                                                                    }
                                                                >
                                                                    Sto{" "}
                                                                    {
                                                                        table.number
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </select>
                                                ) : (
                                                    <div className="list-header-description">
                                                        Lokal nema
                                                        definisane
                                                        stolove.
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    <div className="form-block-2 w-form">
                                        <div className="form-5">
                                            <div className="color-pick-title">
                                                Veličina
                                            </div>

                                            <select
                                                className="form-select qr-code-size w-select"
                                                value={qrSize}
                                                onChange={event =>
                                                    setQrSize(
                                                        Number(
                                                            event
                                                                .target
                                                                .value
                                                        )
                                                    )
                                                }
                                            >
                                                {QR_SIZES.map(
                                                    size => (
                                                        <option
                                                            key={
                                                                size.value
                                                            }
                                                            value={
                                                                size.value
                                                            }
                                                        >
                                                            {
                                                                size.label
                                                            }
                                                        </option>
                                                    )
                                                )}
                                            </select>
                                        </div>
                                    </div>

                                    <div className="color-pick-div">
                                        <div className="color-pick-title">
                                            Boja QR koda
                                        </div>

                                        <div className="color-pick-block">
                                            <div className="color-div-wrapper">
                                                <div
                                                    className="color-div"
                                                    style={{
                                                        backgroundColor:
                                                            safeQrColor,
                                                    }}
                                                >
                                                    <input
                                                        id="qr-color-picker"
                                                        type="color"
                                                        value={
                                                            safeQrColor
                                                        }
                                                        onChange={event =>
                                                            setQrColor(
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            border: "none",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-block w-form">
                                                <div className="form-3">
                                                    <input
                                                        className="form-input color-hex-text w-input"
                                                        type="text"
                                                        value={
                                                            qrColor
                                                        }
                                                        onChange={
                                                            handleQrColorTextChange
                                                        }
                                                        onBlur={() => {
                                                            if (
                                                                !isValidHexColor(
                                                                    qrColor
                                                                )
                                                            ) {
                                                                setQrColor(
                                                                    "#000000"
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="color-pick-div">
                                        <div className="color-pick-title">
                                            Boja pozadine
                                        </div>

                                        <div className="color-pick-block">
                                            <div className="color-div-wrapper">
                                                <div
                                                    className="color-div"
                                                    style={{
                                                        backgroundColor:
                                                            safeBgColor,
                                                    }}
                                                >
                                                    <input
                                                        id="qr-background-picker"
                                                        type="color"
                                                        value={
                                                            safeBgColor
                                                        }
                                                        onChange={event =>
                                                            setBgColor(
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            border: "none",
                                                            cursor: "pointer",
                                                        }}
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-block w-form">
                                                <div className="form-3">
                                                    <input
                                                        className="form-input color-hex-text w-input"
                                                        type="text"
                                                        value={
                                                            bgColor
                                                        }
                                                        onChange={
                                                            handleBgColorTextChange
                                                        }
                                                        onBlur={() => {
                                                            if (
                                                                !isValidHexColor(
                                                                    bgColor
                                                                )
                                                            ) {
                                                                setBgColor(
                                                                    "#ffffff"
                                                                );
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-settings-box-div">
                                <div className="list-header">
                                    <div className="list-header-info">
                                        <div className="list-header-title color">
                                            Link QR koda
                                        </div>

                                        <div className="list-header-description">
                                            Link koji će se otvoriti
                                            nakon skeniranja.
                                        </div>
                                    </div>
                                </div>

                                <div className="qr-code-style-list">
                                    <div className="form-block-2 w-form">
                                        <div className="form-5">
                                            <div className="color-pick-title">
                                                Link
                                            </div>

                                            <div className="div-block-7">
                                                <div className="input-div">
                                                    {currentQrCode?.value ||
                                                        "Podaci se učitavaju..."}
                                                </div>

                                                <div
                                                    className="copy-button-div"
                                                    onClick={
                                                        handleCopyLink
                                                    }
                                                    style={{
                                                        cursor:
                                                            currentQrCode
                                                                ? "pointer"
                                                                : "not-allowed",
                                                    }}
                                                    title="Kopiraj link"
                                                >
                                                    <img
                                                        loading="lazy"
                                                        src={
                                                            CopyIcon
                                                        }
                                                        alt="Kopiraj"
                                                        className="icon-size-20"
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div
                                    className="button-secondary"
                                    onClick={handleShareLink}
                                    style={{
                                        cursor: currentQrCode
                                            ? "pointer"
                                            : "not-allowed",
                                        opacity: currentQrCode
                                            ? 1
                                            : 0.6,
                                    }}
                                >
                                    <img
                                        loading="lazy"
                                        src={ShareIcon}
                                        alt=""
                                        className="button-image"
                                    />

                                    <div>Podeli link</div>
                                </div>
                            </div>

                            <div className="admin-settings-box-div qr-action-block">
                                <div className="list-header-info">
                                    <div className="list-header-title color">
                                        Akcije
                                    </div>

                                    <div className="list-header-description">
                                        Preuzmite, odštampajte ili
                                        izvezite sve QR kodove.
                                    </div>
                                </div>

                                <div className="qr-action-buttons">
                                    <div
                                        className="button-primary"
                                        onClick={handleDownloadPNG}
                                        style={{
                                            cursor: currentQrCode
                                                ? "pointer"
                                                : "not-allowed",
                                        }}
                                    >
                                        <img
                                            loading="lazy"
                                            src={DownloadWhiteIcon}
                                            alt=""
                                            className="button-image"
                                        />

                                        <div>
                                            <strong>
                                                Preuzmi PNG
                                            </strong>
                                        </div>
                                    </div>

                                    <div
                                        className="button-secondary"
                                        onClick={handleDownloadSVG}
                                        style={{
                                            cursor: currentQrCode
                                                ? "pointer"
                                                : "not-allowed",
                                        }}
                                    >
                                        <img
                                            loading="lazy"
                                            src={DownloadBlackIcon}
                                            alt=""
                                            className="button-image"
                                        />

                                        <div>Preuzmi SVG</div>
                                    </div>

                                    <div
                                        className="button-secondary"
                                        onClick={handlePrint}
                                        style={{
                                            cursor: currentQrCode
                                                ? "pointer"
                                                : "not-allowed",
                                        }}
                                    >
                                        <img
                                            loading="lazy"
                                            src={PrinterIcon}
                                            alt=""
                                            className="button-image"
                                        />

                                        <div>Odštampaj</div>
                                    </div>

                                    <div
                                        className="button-secondary"
                                        onClick={
                                            handleDownloadAllQrCodes
                                        }
                                        style={{
                                            cursor:
                                                isExportingAll ||
                                                    !allQrCodes.length
                                                    ? "not-allowed"
                                                    : "pointer",
                                            opacity:
                                                isExportingAll ||
                                                    !allQrCodes.length
                                                    ? 0.6
                                                    : 1,
                                        }}
                                    >
                                        <img
                                            loading="lazy"
                                            src={DownloadBlackIcon}
                                            alt=""
                                            className="button-image"
                                        />

                                        <div>
                                            {isExportingAll
                                                ? "Generisanje..."
                                                : `Preuzmi sve (${allQrCodes.length})`}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="qr-code-view-block">
                            <div className="admin-settings-box-div qr-code-preview">
                                <div className="list-header-info">
                                    <div className="list-header-title color">
                                        {currentQrCode?.title ||
                                            "Pregled QR koda"}
                                    </div>

                                    <div className="list-header-description">
                                        {currentQrCode?.description ||
                                            "Izaberite QR kod koji želite da pregledate."}
                                    </div>
                                </div>

                                <div className="qr-code-preview-div">
                                    <div
                                        style={{
                                            width: 300,
                                            height: 300,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {isLoading ? (
                                            <div>
                                                Učitavanje...
                                            </div>
                                        ) : currentQrCode ? (
                                            <div
                                                style={{
                                                    transform: `scale(${300 /
                                                        qrSize
                                                        })`,
                                                    transformOrigin:
                                                        "center center",
                                                }}
                                            >
                                                <QRCode
                                                    value={
                                                        currentQrCode.value
                                                    }
                                                    size={qrSize}
                                                    fgColor={
                                                        safeQrColor
                                                    }
                                                    bgColor={
                                                        safeBgColor
                                                    }
                                                    level="H"
                                                    includeMargin
                                                />
                                            </div>
                                        ) : (
                                            <div>
                                                QR kod nije
                                                dostupan.
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="div-block-8">
                                    <div className="list-header-description">
                                        {currentQrCode?.description}
                                    </div>

                                    <div className="list-header-description menu-link">
                                        {currentQrCode?.value}
                                    </div>
                                </div>
                            </div>

                            <div className="admin-settings-box-div suggest-how-to-use">
                                <div className="text-block-4">
                                    Saveti za korišćenje
                                </div>

                                <div className="suggest-how-to-use-list">
                                    <div className="suggest-how-to-use-div">
                                        <div className="suggest-how-to-use-number">
                                            <div>1</div>
                                        </div>

                                        <div className="suggest-how-to-use-text">
                                            QR kod za sto sadrži
                                            njegov ID i omogućava
                                            povezivanje porudžbine sa
                                            konkretnim stolom.
                                        </div>
                                    </div>

                                    <div className="suggest-how-to-use-div">
                                        <div className="suggest-how-to-use-number">
                                            <div>2</div>
                                        </div>

                                        <div className="suggest-how-to-use-text">
                                            ZIP izvoz sadrži meni,
                                            stranicu sa linkovima i
                                            QR kod za svaki sto.
                                        </div>
                                    </div>

                                    <div className="suggest-how-to-use-div">
                                        <div className="suggest-how-to-use-number">
                                            <div>3</div>
                                        </div>

                                        <div className="suggest-how-to-use-text">
                                            Za profesionalnu štampu
                                            koristite SVG format jer
                                            ne gubi kvalitet.
                                        </div>
                                    </div>

                                    <div className="suggest-how-to-use-div">
                                        <div className="suggest-how-to-use-number">
                                            <div>4</div>
                                        </div>

                                        <div className="suggest-how-to-use-text">
                                            Pre postavljanja na
                                            stolove proverite nekoliko
                                            QR kodova različitim
                                            telefonima.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default QRCodePage;